import './art-lightbox.component.scss';

import type { FunctionComponent } from 'preact';
import { h } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useRef } from 'preact/hooks';

import { IconClose } from '../../../common/icons/icon-close';
import { useTranslation } from '../../../i18n';

export interface ArtLightboxImage {
  url: string;
  label?: string;
}

export interface ArtLightboxProps {
  /** Sized for a single image today; paging between images (back cover,
   *  liner notes) is planned, so callers already pass an array. */
  images: ArtLightboxImage[];
  startIndex?: number;
  onClose: () => void;
}

const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const TAP_MS = 250;
const TAP_SLOP_PX = 10;
const DOUBLE_TAP_MS = 300;
const DISMISS_PX = 80;

export const ArtLightbox: FunctionComponent<ArtLightboxProps> = ({
  images,
  startIndex,
  onClose,
}) => {
  const { t } = useTranslation();
  const stageRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const image = images[Math.min(Math.max(startIndex ?? 0, 0), images.length - 1)];

  // Pinch/pan/double-tap/swipe-dismiss. Attached imperatively: iOS only
  // honors preventDefault on non-passive touchmove listeners, and JSX
  // handlers can't opt out of passive. Transforms are applied inline so
  // gestures never re-render.
  useEffect(() => {
    const stage = stageRef.current;
    const img = imgRef.current;
    if (!stage || !img) return;

    let scale = 1;
    let tx = 0;
    let ty = 0;
    let s0 = 1;
    let tx0 = 0;
    let ty0 = 0;
    let startDist = 1;
    let startMid = { x: 0, y: 0 };
    let startTouch = { x: 0, y: 0 };
    let startAt = 0;
    let lastTapAt = 0;
    let mode: 'none' | 'pinch' | 'pan' | 'dismiss' = 'none';

    // Coordinates relative to the stage center, matching transform-origin.
    const rel = (t: Touch) => {
      const r = stage.getBoundingClientRect();
      return { x: t.clientX - (r.left + r.width / 2), y: t.clientY - (r.top + r.height / 2) };
    };
    const midOf = (a: Touch, b: Touch) => {
      const pa = rel(a);
      const pb = rel(b);
      return { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 };
    };
    const distOf = (a: Touch, b: Touch) =>
      Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) || 1;

    const clampPan = () => {
      const maxX = Math.max(0, (img.offsetWidth * scale - stage.clientWidth) / 2);
      const maxY = Math.max(0, (img.offsetHeight * scale - stage.clientHeight) / 2);
      tx = Math.min(maxX, Math.max(-maxX, tx));
      ty = Math.min(maxY, Math.max(-maxY, ty));
    };
    const apply = (animated: boolean) => {
      img.style.transition = animated ? 'transform 0.25s ease' : 'none';
      img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
    };
    const reset = (animated: boolean) => {
      scale = 1;
      tx = 0;
      ty = 0;
      apply(animated);
    };
    const settle = () => {
      if (scale <= 1.02) {
        reset(true);
      } else {
        clampPan();
        apply(true);
      }
    };

    const handleTap = (t: Touch) => {
      const now = Date.now();
      const isDouble = now - lastTapAt < DOUBLE_TAP_MS;
      lastTapAt = isDouble ? 0 : now;

      if (isDouble) {
        if (scale > 1) {
          reset(true);
        } else {
          const p = rel(t);
          scale = DOUBLE_TAP_SCALE;
          tx = -(DOUBLE_TAP_SCALE - 1) * p.x;
          ty = -(DOUBLE_TAP_SCALE - 1) * p.y;
          clampPan();
          apply(true);
        }
        return;
      }

      const r = img.getBoundingClientRect();
      const insideImg =
        t.clientX >= r.left && t.clientX <= r.right && t.clientY >= r.top && t.clientY <= r.bottom;
      if (!insideImg && scale === 1) onCloseRef.current();
    };

    const start = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        mode = 'pinch';
        s0 = scale;
        tx0 = tx;
        ty0 = ty;
        startDist = distOf(e.touches[0], e.touches[1]);
        startMid = midOf(e.touches[0], e.touches[1]);
      } else if (e.touches.length === 1) {
        mode = scale > 1 ? 'pan' : 'dismiss';
        tx0 = tx;
        ty0 = ty;
        startTouch = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        startAt = Date.now();
      }
    };

    const move = (e: TouchEvent) => {
      if (mode === 'none') return;
      e.preventDefault();
      if (mode === 'pinch' && e.touches.length === 2) {
        const m = midOf(e.touches[0], e.touches[1]);
        scale = Math.min(
          MAX_SCALE,
          Math.max(1, (s0 * distOf(e.touches[0], e.touches[1])) / startDist)
        );
        // Keep the pinch midpoint anchored to the same image point while
        // scaling, and let midpoint movement pan at the same time.
        tx = m.x - (scale / s0) * (startMid.x - tx0);
        ty = m.y - (scale / s0) * (startMid.y - ty0);
        clampPan();
        apply(false);
      } else if (mode === 'pan' && e.touches.length === 1) {
        tx = tx0 + (e.touches[0].clientX - startTouch.x);
        ty = ty0 + (e.touches[0].clientY - startTouch.y);
        clampPan();
        apply(false);
      } else if (mode === 'dismiss' && e.touches.length === 1) {
        ty = Math.max(0, e.touches[0].clientY - startTouch.y);
        apply(false);
      }
    };

    const end = (e: TouchEvent) => {
      if (mode === 'none') return;
      if (e.touches.length > 0) {
        // A finger lifted mid-gesture: rebase on whatever remains.
        start(e);
        return;
      }
      const wasMode = mode;
      mode = 'none';

      if (wasMode === 'dismiss') {
        const t = e.changedTouches[0];
        const dy = t ? t.clientY - startTouch.y : 0;
        const dx = t ? Math.abs(t.clientX - startTouch.x) : 0;
        const isTap =
          t && Date.now() - startAt < TAP_MS && Math.abs(dy) < TAP_SLOP_PX && dx < TAP_SLOP_PX;
        if (isTap) {
          ty = 0;
          apply(false);
          handleTap(t);
        } else if (dy > DISMISS_PX) {
          onCloseRef.current();
        } else {
          reset(true);
        }
        return;
      }

      settle();
      if (wasMode === 'pan') {
        const t = e.changedTouches[0];
        const isTap =
          t &&
          Date.now() - startAt < TAP_MS &&
          Math.abs(t.clientX - startTouch.x) < TAP_SLOP_PX &&
          Math.abs(t.clientY - startTouch.y) < TAP_SLOP_PX;
        if (isTap) handleTap(t);
      }
    };

    const cancel = () => {
      mode = 'none';
      settle();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };

    stage.addEventListener('touchstart', start, { passive: true });
    stage.addEventListener('touchmove', move, { passive: false });
    stage.addEventListener('touchend', end);
    stage.addEventListener('touchcancel', cancel);
    window.addEventListener('keydown', onKey);
    return () => {
      stage.removeEventListener('touchstart', start);
      stage.removeEventListener('touchmove', move);
      stage.removeEventListener('touchend', end);
      stage.removeEventListener('touchcancel', cancel);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  if (!image) return null;

  return createPortal(
    <div class="single-art-lightbox" role="dialog" aria-modal="true">
      <button
        type="button"
        class="single-art-lightbox-close"
        aria-label={t('common.close')}
        onClick={() => onClose()}
      >
        <IconClose width={16} height={16} />
      </button>
      <div
        class="single-art-lightbox-stage"
        ref={stageRef}
        // Mouse counterpart of the tap-outside dismiss (touch taps synthesize a
        // click too, but by then onClose already ran and the portal is gone)
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <img ref={imgRef} src={image.url} alt={image.label ?? ''} draggable={false} />
      </div>
    </div>,
    document.body
  );
};
